import { useLocation } from "wouter";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Edit } from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import { useAuth } from "@/lib/auth-context";

export default function ViewStarterMaterial() {
  const [location, setLocation] = useLocation();
  const [materialId, setMaterialId] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    setMaterialId(id);
  }, [location]);

  const { data: materialsData, isLoading: materialsLoading } = useQuery({
    queryKey: ["/api/starter-pack/materials"],
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["/api/starter-pack/categories/materials"],
  });

  const materials = Array.isArray(materialsData) ? materialsData : [];
  const categories = Array.isArray(categoriesData) ? categoriesData : [];

  const material = materialId && Array.isArray(materials)
    ? materials.find((m: any) => m._id === materialId || m.id === materialId)
    : undefined;

  const category = material?.categoryId && Array.isArray(categories)
    ? categories.find((c: any) => c._id === material.categoryId)?.name || 'Uncategorized'
    : 'Uncategorized';

  if (materialsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!material) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setLocation("/starter-pack")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl sm:text-3xl font-bold">Material not found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setLocation("/starter-pack")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-3xl font-bold tracking-tight">{material.name}</h1>
            <p className="text-muted-foreground">Starter Pack Material</p>
          </div>
        </div>
        <Button onClick={() => setLocation(`/starter-pack/material?edit=${material._id || material.id}`)}>
          <Edit className="h-4 w-4 mr-2" />
          Edit
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Name</label>
              <p className="text-lg">{material.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Category</label>
              <p className="text-lg">{category}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Unit</label>
              <p className="text-lg">{material.unit || 'N/A'}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pricing Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Purchase Amount</label>
              <p className="text-lg">{formatCurrency(Number(material.purchaseAmount || 0), user?.currency || "USD")}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Price per Unit</label>
              <p className="text-lg">{formatCurrency(Number(material.price || 0), user?.currency || "USD")}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {(material.image || material.imageUrl) && (
        <Card>
          <CardHeader>
            <CardTitle>Image</CardTitle>
          </CardHeader>
          <CardContent>
            <img
              src={material.image || material.imageUrl}
              alt={material.name}
              className="w-full max-w-md mx-auto rounded-lg object-cover"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
